import type { KeyboardEvent, PointerEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import type {
  IGlobalSearchHighlight,
  IUseGlobalSearchHighlightProps,
} from "../types";

// NOTE: cmdk's own selectable-row selector and the attribute it keeps each
// row's value in.
const SELECTABLE_ITEM_SELECTOR = '[cmdk-item=""]:not([aria-disabled="true"])';
const ITEM_VALUE_ATTRIBUTE = "data-value";

// NOTE: The keys cmdk moves the highlight with; Ctrl+J/K/N/P are its vim
// bindings, on by default.
const NAVIGATION_KEYS: ReadonlySet<string> = new Set([
  "ArrowDown",
  "ArrowUp",
  "Home",
  "End",
]);
const VIM_NAVIGATION_KEYS: ReadonlySet<string> = new Set(["j", "k", "n", "p"]);

const isNavigationKey = (event: KeyboardEvent<HTMLDivElement>): boolean =>
  NAVIGATION_KEYS.has(event.key) ||
  (event.ctrlKey && VIM_NAVIGATION_KEYS.has(event.key));

// NOTE: NSAI-6762 — cmdk highlights the first row only when the query text
// changes or when nothing is highlighted yet. The fallback actions render on
// the keystroke and take that highlight, so chats and /search/global results
// arriving above them after the debounce never got it and Enter ran an action.
// The palette therefore controls the highlight: after every render it goes to
// the first row, until the user moves it themselves (arrow keys or the mouse).
// Any change of the query hands it back to the first row — and, since
// NSAI-6853, any change of the category tab.
export const useGlobalSearchHighlight = ({
  query,
  tab,
  leadingGroups,
  trailingGroups,
  chats,
  semanticHits,
}: IUseGlobalSearchHighlightProps): IGlobalSearchHighlight => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState("");
  const isPinnedRef = useRef(false);
  const resetKey = `${tab}\u0000${query}`;
  const lastResetKeyRef = useRef(resetKey);
  // NOTE: True only while a navigation key or pointer move is being handled.
  // cmdk moves the highlight synchronously inside that event, so a value change
  // seen meanwhile is the user's; a key that moves nothing (ArrowUp on the
  // first row) never reaches onValueChange and pins nothing.
  const isUserMoveRef = useRef(false);

  // NOTE: The row sources are dependencies only — the first row is read from
  // the DOM, where every source has already rendered in palette order.
  useEffect(() => {
    if (lastResetKeyRef.current !== resetKey) {
      lastResetKeyRef.current = resetKey;
      isPinnedRef.current = false;
    }
    if (isPinnedRef.current) return;
    const firstValue = rootRef.current
      ?.querySelector(SELECTABLE_ITEM_SELECTOR)
      ?.getAttribute(ITEM_VALUE_ATTRIBUTE);
    if (firstValue) setValue(firstValue);
  }, [resetKey, leadingGroups, trailingGroups, chats, semanticHits]);

  const markUserMove = useCallback((): void => {
    isUserMoveRef.current = true;
    // HACK: A task, not a microtask — React handles the capture and bubble
    // phases in separate native listeners, and microtasks run between them,
    // which would drop the flag before the row's pointermove handler runs.
    setTimeout(() => {
      isUserMoveRef.current = false;
    }, 0);
  }, []);

  const handleValueChange = useCallback((nextValue: string): void => {
    if (isUserMoveRef.current) isPinnedRef.current = true;
    setValue(nextValue);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (isNavigationKey(event)) markUserMove();
    },
    [markUserMove],
  );

  // NOTE: Capture phase, so the flag is up before the row's own pointermove
  // handler moves the highlight. Rows sliding under a still cursor fire no
  // pointer events, so only a real move counts.
  const handlePointerMoveCapture = useCallback(
    (event: PointerEvent<HTMLDivElement>): void => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest(SELECTABLE_ITEM_SELECTOR)) markUserMove();
    },
    [markUserMove],
  );

  return {
    rootRef,
    value,
    onValueChange: handleValueChange,
    onKeyDown: handleKeyDown,
    onPointerMoveCapture: handlePointerMoveCapture,
  };
};
